/**
 * Client settings store — the single source of truth for every player-tunable RENDER / AUDIO / INPUT
 * preference (Settings chapter of the Captain's Book). 100% client-side & cosmetic: nothing here touches
 * the wire, the sim or the server. Defaults reproduce the game's CURRENT behaviour exactly, so a player who
 * never opens Settings notices no difference.
 *
 * Persisted as versioned JSON in localStorage['sor:settings']. Migration is tolerant: an unknown key is
 * ignored, a missing key falls back to its default, an out-of-range value is clamped — a corrupt blob just
 * yields the defaults. The pure {@link coerceSettings}/{@link parseSettings}/{@link volumeGain} helpers carry
 * no side effects (no localStorage, no DOM) so they run under the node test environment.
 */

export type GraphicsPreset = 'low' | 'medium' | 'high';
export const GRAPHICS_PRESETS: readonly GraphicsPreset[] = ['low', 'medium', 'high'];

export interface Settings {
  /** Schema version — bumped only if the shape changes; old blobs migrate through {@link coerceSettings}. */
  version: number;
  /** Render quality preset. `medium` == the game's shipped/default renderer behaviour. */
  graphics: GraphicsPreset;
  /** Overall output volume, 0..100. */
  volMaster: number;
  /** One-shot sound effects volume (cannons, splashes, coins, UI), 0..100. */
  volSfx: number;
  /** Ambiance + music beds volume (ocean, storm, port, tavern, soundtrack), 0..100. */
  volAmbiance: number;
  /** Incoming voice-chat volume (Onda Social), 0..100. */
  volVoice: number;
  /** Global mute — mirrors the sea-HUD 🔊 toggle. */
  muted: boolean;
  /** Sea-camera drag sensitivity, 0.5×..2.0×. 1.0 == shipped feel. */
  camSensitivity: number;
  /** Invert the horizontal camera-drag direction. false == shipped WoW/L2 convention (drag right → starboard). */
  invertCam: boolean;
  /** Auto-enter browser fullscreen on SET SAIL. true == shipped behaviour. Turning it off (or leaving
   *  fullscreen manually) stops the game re-forcing it — see main.ts's fullscreen golden rule. */
  fullscreen: boolean;
}

export const SETTINGS_VERSION = 1;
const STORAGE_KEY = 'sor:settings';

export const DEFAULT_SETTINGS: Settings = {
  version: SETTINGS_VERSION,
  graphics: 'medium',
  volMaster: 100,
  volSfx: 100,
  volAmbiance: 100,
  volVoice: 100,
  muted: false,
  camSensitivity: 1.0,
  invertCam: false,
  fullscreen: true,
};

export const CAM_SENS_MIN = 0.5, CAM_SENS_MAX = 2.0;

/* ------------------------------- pure helpers (side-effect free) ------------------------------- */

const clampInt = (n: unknown, lo: number, hi: number, fallback: number): number => {
  const v = typeof n === 'number' && Number.isFinite(n) ? Math.round(n) : NaN;
  if (Number.isNaN(v)) return fallback;
  return v < lo ? lo : v > hi ? hi : v;
};

const clampFloat = (n: unknown, lo: number, hi: number, fallback: number): number => {
  const v = typeof n === 'number' && Number.isFinite(n) ? n : NaN;
  if (Number.isNaN(v)) return fallback;
  return v < lo ? lo : v > hi ? hi : v;
};

/**
 * Fold an arbitrary parsed blob (any shape, any version) into a valid {@link Settings}. Missing keys take
 * their default; wrong-typed / out-of-range values are clamped or fall back; unknown keys are dropped. The
 * result always carries the current {@link SETTINGS_VERSION}. `base` supplies the fallbacks (defaults, or the
 * live settings when patching a single field).
 */
export function coerceSettings(raw: unknown, base: Settings = DEFAULT_SETTINGS): Settings {
  const r = (raw && typeof raw === 'object') ? (raw as Record<string, unknown>) : {};
  const graphics = GRAPHICS_PRESETS.includes(r.graphics as GraphicsPreset) ? (r.graphics as GraphicsPreset) : base.graphics;
  return {
    version: SETTINGS_VERSION,
    graphics,
    volMaster: clampInt(r.volMaster, 0, 100, base.volMaster),
    volSfx: clampInt(r.volSfx, 0, 100, base.volSfx),
    volAmbiance: clampInt(r.volAmbiance, 0, 100, base.volAmbiance),
    volVoice: clampInt(r.volVoice, 0, 100, base.volVoice),
    muted: typeof r.muted === 'boolean' ? r.muted : base.muted,
    camSensitivity: clampFloat(r.camSensitivity, CAM_SENS_MIN, CAM_SENS_MAX, base.camSensitivity),
    invertCam: typeof r.invertCam === 'boolean' ? r.invertCam : base.invertCam,
    fullscreen: typeof r.fullscreen === 'boolean' ? r.fullscreen : base.fullscreen,
  };
}

/** Parse a stored JSON string (or null) into valid Settings; any error yields the defaults. */
export function parseSettings(json: string | null | undefined): Settings {
  if (!json) return { ...DEFAULT_SETTINGS };
  try { return coerceSettings(JSON.parse(json)); } catch { return { ...DEFAULT_SETTINGS }; }
}

export function serializeSettings(s: Settings): string { return JSON.stringify(s); }

/**
 * Map a 0..100 volume slider to a linear-gain multiplier with a gentle perceptual curve (hearing is roughly
 * logarithmic, so a straight ratio feels top-heavy). 0 → silent, 100 → 1.0 (unchanged shipped level).
 */
export function volumeGain(pct: number): number {
  const p = pct <= 0 ? 0 : pct >= 100 ? 100 : pct;
  return p <= 0 ? 0 : Math.pow(p / 100, 1.6);
}

/* --------------------------------- the store (stateful) --------------------------------- */

type Listener = (s: Settings, key: keyof Settings | null) => void;

function readStorage(): Settings {
  try {
    if (typeof localStorage === 'undefined') return { ...DEFAULT_SETTINGS };
    return parseSettings(localStorage.getItem(STORAGE_KEY));
  } catch { return { ...DEFAULT_SETTINGS }; }
}

let current: Settings = readStorage();
const listeners = new Set<Listener>();

function persist(): void {
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, serializeSettings(current)); }
  catch { /* private-mode / quota — settings still live in memory for this session */ }
}

/** A defensive copy of the whole settings object. */
export function getSettings(): Settings { return { ...current }; }

export function getSetting<K extends keyof Settings>(key: K): Settings[K] { return current[key]; }

/**
 * Update one setting (validated + clamped), persist, and notify listeners — but only if the value actually
 * changed. Returns the (possibly clamped) stored value.
 */
export function setSetting<K extends keyof Settings>(key: K, value: Settings[K]): Settings[K] {
  const next = coerceSettings({ ...current, [key]: value }, current);
  if (next[key] === current[key]) return current[key];
  current = next;
  persist();
  for (const cb of listeners) cb(current, key);
  return current[key];
}

/** Subscribe to changes; returns an unsubscribe fn. */
export function onSettingChange(cb: Listener): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** TEST-ONLY: reset the in-memory store to defaults (does not touch localStorage). */
export function __resetSettingsForTest(): void { current = { ...DEFAULT_SETTINGS }; listeners.clear(); }
